import { createRoot } from "react-dom/client";
import { useEffect, useState } from "react";

// Imperative replacements for window.confirm / window.alert, styled to match
// the admin design. confirmDialog() resolves true/false; toast() shows a
// transient top-right notice.

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  /** red confirm button + warning icon for destructive actions */
  danger?: boolean;
}

function ConfirmCard({
  opts,
  onDone,
}: {
  opts: ConfirmOptions;
  onDone: (ok: boolean) => void;
}) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    requestAnimationFrame(() => setShown(true));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDone(false);
      if (e.key === "Enter") onDone(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDone]);

  const danger = opts.danger ?? true;

  return (
    <div
      className={`fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-4 pt-[max(env(safe-area-inset-top),12px)] pb-[max(env(safe-area-inset-bottom),12px)] transition-opacity duration-150 ${shown ? "opacity-100" : "opacity-0"}`}
    >
      <div className="absolute inset-0 bg-black/35" onClick={() => onDone(false)} />
      <div
        className={`relative w-full max-w-sm max-h-[calc(100dvh-24px)] bg-white rounded-2xl shadow-2xl p-5 sm:p-6 overflow-y-auto overscroll-contain transition-transform duration-150 ${shown ? "scale-100" : "scale-95"}`}
      >
        <div className="flex items-start gap-3.5">
          <div
            className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${danger ? "bg-red-50" : "bg-teal-50"}`}
          >
            <i className={`${danger ? "ri-alert-line text-red-500" : "ri-question-line text-teal-600"} text-xl`} />
          </div>
          <div className="min-w-0 pt-0.5">
            <h3 className="text-base font-semibold text-gray-900">{opts.title ?? "确认操作"}</h3>
            <p className="text-sm text-gray-500 mt-1.5 leading-relaxed whitespace-pre-wrap">{opts.message}</p>
          </div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row gap-2.5 mt-6">
          <button
            onClick={() => onDone(false)}
            className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            {opts.cancelText ?? "取消"}
          </button>
          <button
            autoFocus
            onClick={() => onDone(true)}
            className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors ${
              danger ? "bg-red-500 hover:bg-red-600" : "bg-teal-600 hover:bg-teal-700"
            }`}
          >
            {opts.confirmText ?? "确认"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const host = document.createElement("div");
    document.body.appendChild(host);
    const root = createRoot(host);
    const done = (ok: boolean) => {
      root.unmount();
      host.remove();
      resolve(ok);
    };
    root.render(<ConfirmCard opts={opts} onDone={done} />);
  });
}

// ---- toast ----

type ToastType = "success" | "error" | "info";

const TOAST_STYLE: Record<ToastType, { icon: string; cls: string }> = {
  success: { icon: "ri-checkbox-circle-fill text-emerald-500", cls: "border-emerald-200" },
  error: { icon: "ri-close-circle-fill text-red-500", cls: "border-red-200" },
  info: { icon: "ri-information-fill text-teal-500", cls: "border-teal-200" },
};

let toastHost: HTMLDivElement | null = null;

function ensureToastHost(): HTMLDivElement {
  if (!toastHost || !document.body.contains(toastHost)) {
    toastHost = document.createElement("div");
    toastHost.className = "fixed top-5 right-5 z-[90] flex flex-col gap-2 items-end pointer-events-none";
    document.body.appendChild(toastHost);
  }
  return toastHost;
}

export function toast(message: string, type: ToastType = "info", durationMs = 3200) {
  const host = ensureToastHost();
  const el = document.createElement("div");
  const { icon, cls } = TOAST_STYLE[type];
  el.className = `pointer-events-auto flex items-start gap-2 max-w-sm bg-white border ${cls} shadow-lg rounded-xl px-4 py-3 text-sm text-gray-700 transition-all duration-200 translate-x-3 opacity-0`;
  el.innerHTML = `<i class="${icon} text-base flex-shrink-0"></i><span class="whitespace-pre-wrap break-words"></span>`;
  (el.lastElementChild as HTMLElement).textContent = message;
  host.appendChild(el);
  requestAnimationFrame(() => el.classList.remove("translate-x-3", "opacity-0"));
  const close = () => {
    el.classList.add("opacity-0", "translate-x-3");
    setTimeout(() => el.remove(), 220);
  };
  el.addEventListener("click", close);
  setTimeout(close, durationMs);
}
